import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, alpha, useTheme, Divider, Stack } from '@mui/material';
import { Cake, CalendarTick } from 'iconsax-react';
import useDateSummaryDialogStyles from './useDateSummaryDialogStyles';
import { useTranslation } from 'react-i18next';
import i18n from '@application/utilities/i18n';
import { useNavigate } from 'react-router-dom';
const SelectedDateSummary = ({ date, birthdaysCount, anniversariesCount, birthdayPeople = [], anniversaryPeople = [], birthdayItems, anniversaryItems, listMaxHeight = '150px' }) => {
    const { t } = useTranslation();
    const styles = useDateSummaryDialogStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const handlePersonClick = (id) => {
        if (id) {
            navigate(`/people/${id}`);
        }
    };
    const title = date.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
    const resolvedBirthdayItems = birthdayItems ?? birthdayPeople.map((p) => ({ id: p.id, text: p.name })) ?? [];
    const resolvedAnniversaryItems = anniversaryItems ?? anniversaryPeople.map((p) => ({ id: p.id, text: p.name })) ?? [];
    const paletteFor = (listType) => listType === 'birthday' ? theme.palette.success : theme.palette.info;
    const emptyLabelFor = (listType) => listType === 'birthday' ? t('people.noBirthdays') : t('people.noAnniversaries');
    const EmptyState = ({ listType }) => (_jsx(Box, { sx: {
            p: 1.25,
            borderRadius: 1.5,
            border: `1px dashed ${alpha(paletteFor(listType).main, 0.4)}`,
            backgroundColor: alpha(paletteFor(listType).main, 0.04)
        }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: emptyLabelFor(listType) }) }));
    const renderTextList = (items, listType) => {
        const pal = paletteFor(listType);
        if (!items.length)
            return _jsx(EmptyState, { listType: listType });
        return (_jsx(Box, { sx: {
                maxHeight: listMaxHeight,
                overflowY: 'auto',
                pr: 0.5
            }, children: _jsx(Stack, { spacing: 1, children: items.map((item, idx) => {
                    const nameToHighlight = item.personName;
                    let beforeName = '';
                    let afterName = item.text;
                    if (nameToHighlight && item.text.includes(nameToHighlight)) {
                        const nameIndex = item.text.indexOf(nameToHighlight);
                        beforeName = item.text.substring(0, nameIndex);
                        afterName = item.text.substring(nameIndex + nameToHighlight.length);
                    }
                    return (_jsxs(Box, { sx: {
                            display: 'flex',
                            gap: 1,
                            p: 1.25,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(pal.main, 0.28)}`,
                            backgroundColor: alpha(pal.main, 0.06),
                            transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                            cursor: 'default'
                        }, children: [_jsx(Box, { sx: {
                                    width: 6,
                                    borderRadius: 1,
                                    backgroundColor: pal.main,
                                    flexShrink: 0
                                } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: 'body2', sx: { whiteSpace: 'pre-wrap', lineHeight: 1.5 }, children: nameToHighlight && item.id ? (_jsxs(_Fragment, { children: [beforeName, _jsx(Typography, { component: 'span', variant: 'body2', onClick: (e) => {
                                                        e.stopPropagation();
                                                        handlePersonClick(item.id);
                                                    }, sx: {
                                                        color: pal.dark,
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }, children: nameToHighlight }), afterName] })) : (item.text) }), (item.metaLeft || item.metaRight) && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }, children: [item.metaLeft && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: item.metaLeft })), item.metaLeft && item.metaRight && (_jsx(Box, { sx: {
                                                    width: 4,
                                                    height: 4,
                                                    borderRadius: '50%',
                                                    backgroundColor: pal.main,
                                                    flexShrink: 0
                                                } })), item.metaRight && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: item.metaRight }))] }))] })] }, item.id ?? `${item.text}-${idx}`));
                }) }) }));
    };
    return (_jsxs(Box, { sx: {
            ...styles.dateFrame,
            backgroundColor: alpha(theme.palette.background.paper, 0.92)
        }, children: [_jsx(Box, { className: 'box_top', sx: { ...styles.boxTop }, children: _jsx(Typography, { variant: 'h6', sx: { fontWeight: 600 }, children: title }) }), _jsx(Divider, {}), _jsxs(Box, { className: 'box_bottom', sx: {
                    ...styles.boxBottom,
                    mt: 2,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 2
                }, children: [birthdaysCount === 0 && anniversariesCount === 0 && (_jsx(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 100
                        }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('people.noBirthdaysAndAnniversaries') }) })), birthdaysCount > 0 && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Cake, { variant: 'Bulk', size: '24', color: theme.palette.success.dark, style: { flexShrink: 0 } }), _jsxs(Typography, { variant: 'body1', children: [t('people.birthdays'), ": ", _jsx("b", { children: birthdaysCount })] })] }), renderTextList(resolvedBirthdayItems, 'birthday')] })), anniversariesCount > 0 && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(CalendarTick, { variant: 'Bulk', size: '24', color: theme.palette.info.dark, style: { flexShrink: 0 } }), _jsxs(Typography, { variant: 'body1', children: [t('people.anniversaries'), ": ", _jsx("b", { children: anniversariesCount })] })] }), renderTextList(resolvedAnniversaryItems, 'anniversary')] }))] })] }));
};
export default SelectedDateSummary;
